import {
  compareDesc,
  isSameDay,
  isWithinRange,
  startOfDay,
  startOfYesterday,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';

export function isToday(date: string | Date) {
  return isSameDay(new Date(), date);
}

function isYesterday(date: string | Date) {
  return isSameDay(subDays(new Date(), 1), date);
}

function startOfOneWeekAgo() {
  return startOfDay(subWeeks(new Date(), 1));
}

function startOfOneMonthAgo() {
  return startOfDay(subMonths(new Date(), 1));
}

function isPastWeek(date: string | Date) {
  return isWithinRange(date, startOfOneWeekAgo(), startOfYesterday());
}

function isPastMonth(date: string | Date) {
  return isWithinRange(date, startOfOneMonthAgo(), startOfOneWeekAgo());
}

export interface TimedItem {
  timestamp: string | Date;
}

export enum DateGroup {
  today = 'today',
  yesterday = 'yesterday',
  pastWeek = 'pastWeek',
  pastMonth = 'pastMonth',
  older = 'older',
}

export interface DateGroupItems<AnyItem> {
  group: DateGroup;
  items: AnyItem[];
  time: Date;
}

function getDateGroup(item: TimedItem) {
  if (isToday(item.timestamp)) {
    return DateGroup.today;
  } else if (isYesterday(item.timestamp)) {
    return DateGroup.yesterday;
  } else if (isPastWeek(item.timestamp)) {
    return DateGroup.pastWeek;
  } else if (isPastMonth(item.timestamp)) {
    return DateGroup.pastMonth;
  } else {
    return DateGroup.older;
  }
}

function groupTimedItems<AnyItem extends TimedItem>(
  items: AnyItem[],
  getGroupKey: (item: AnyItem, group: DateGroup, time: number) => string | number,
) {
  const groupedItems: { [key: string]: DateGroupItems<AnyItem> } = {};

  items.forEach(item => {
    const group = getDateGroup(item);
    const time: number = startOfDay(item.timestamp).getTime();

    const groupKey = getGroupKey(item, group, time);

    if (groupedItems[groupKey]) {
      groupedItems[groupKey].items.push(item);
    } else {
      groupedItems[groupKey] = {
        group: getDateGroup(item),
        items: [item],
        time: new Date(time),
      };
    }
  });

  return Object.values(groupedItems).sort((a, b) => compareDesc(a.time, b.time));
}

export function groupTimedItemsByGroup<AnyItem extends TimedItem>(items: AnyItem[]): DateGroupItems<AnyItem>[] {
  return groupTimedItems<AnyItem>(items, (item, group) => group);
}
